<script lang="ts" setup>
  import { computed, resolveComponent } from '#imports'
  import { ContentBlockTypeId } from '~/model/ContentBlockData/ContentBlockData'
  import { ContentBlockFacet } from '~/components/ContentBlock/ContentBlockFacet'
  import { ContentPageContent } from '~/model/ContentPage/ContentPage'
  import useMarkdown from '~/composables/Markdown/Markdown'
  import { VisualThemeId } from '~/model/VisualTheme/VisualTheme'

  interface Props {
    content: ContentPageContent
  }

  const ContentBlockFile = resolveComponent('ContentBlockFile') as string
  const ContentBlockImage = resolveComponent('ContentBlockImage') as string
  const ContentBlockIntroduction = resolveComponent('ContentBlockIntroduction') as string
  const ContentBlockRichText = resolveComponent('ContentBlockRichText') as string

  const { parseMarkdown } = useMarkdown()

  const props = defineProps<Props>()

  const hasAside = computed<boolean>(() => {
    return Array.isArray(props.content.asideList) && props.content.asideList.length > 0
  })

  const getComponent = (type: ContentBlockTypeId) => {
    switch (type) {
      case ContentBlockTypeId.Image:
        return ContentBlockImage
      case ContentBlockTypeId.File:
        return ContentBlockFile
      case ContentBlockTypeId.Introduction:
        return ContentBlockIntroduction
      case ContentBlockTypeId.RichText:
        return ContentBlockRichText
    }
  }
</script>

<template>
  <div class="c-content-page-body">
    <div class="c-content-page-body__header">
      <h1 class="c-content-page-body__title">
        {{ content.name }}
      </h1>
      <div
        v-if="content.introduction"
        class="c-content-page-body__introduction u-markdown-root"
        v-html="parseMarkdown(content.introduction)"
      />
    </div>
    <div class="c-content-page-body__structure">
      <aside v-if="hasAside" class="s-grid s-grid--aside c-content-page-body__aside-blocks">
        <component
          :is="getComponent(type)"
          v-for="({ facets, type, ...block }, blockIdx) in content.asideList"
          :key="blockIdx"
          :facets="[VisualThemeId.Negative, ContentBlockFacet.InAside, ...(facets ?? [])]"
          :type="type"
          v-bind="block"
        />
      </aside>
      <main class="s-grid s-grid--primary c-content-page-body__main-blocks">
        <component
          :is="getComponent(type)"
          v-for="({ facets, type, ...block }, blockIdx) in content.contentList"
          :key="blockIdx"
          :facets="[ContentBlockFacet.InContent, ...(facets ?? [])]"
          :type="type"
          v-bind="block"
        />
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/base/mq' as mq;

  .c-content-page-body {
    padding: 100px 0 100px;

    @include mq.media('<=sm') {
      padding: 60px 0 60px;
    }
  }

  .c-content-page-body__header {
    max-width: 800px;
    margin-bottom: 60px;

    @include mq.media('<=sm') {
      margin-bottom: 40px;
    }
  }

  .c-content-page-body__structure {
    display: grid;
    grid-template-columns: 2fr 4fr;
    gap: 40px;
    align-items: start;

    @include mq.media('<=sm') {
      grid-template-columns: 1fr 1fr;
    }
  }

  .c-content-page-body__aside-blocks,
  .c-content-page-body__main-blocks {
    @include mq.media('<=sm') {
      grid-column: 1 / 3;
    }
  }

  .c-content-page-body__main-blocks:only-child {
    grid-column: 1 / 3;
  }

  .c-content-page-body__title {
    margin-bottom: 24px;
  }

  .c-content-page-body__aside-blocks,
  .c-content-page-body__main-blocks {
    row-gap: 40px;

    @include mq.media('<=sm') {
      row-gap: 32px;
    }
  }
</style>

<style lang="scss">
  @use '/assets/scss/base/typography/typography' as type;

  .c-content-page-body__introduction {
    &.u-markdown-root.u-markdown-root > p {
      @include type.copy-large;
    }
  }
</style>
